import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { Loader, LoaderStages } from "@/components/Loader";
import { ArrowLeft, Sparkles, Users, Crown, ShieldCheck, KeyRound, Wallet, Wrench } from "lucide-react";

/**
 * Stakeholder map — for a given target firm (FIRM_B), identifies the 5 roles
 * inside the target that need to be engaged to land the partnership. Each row
 * shows: role badge + likely job title + department + why it matters + first-contact
 * angle + a list of LinkedIn-searchable alt titles.
 *
 * URL: /app/firms/:id/stakeholders
 */

// role -> icon + colored border. Roles are stable enums from the backend prompt.
const ROLE_META = {
    decision_maker: { icon: Crown, label: "Decision maker", tone: "border-amber-500/50 bg-amber-500/10 text-amber-300" },
    champion: { icon: Sparkles, label: "Champion", tone: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" },
    gatekeeper: { icon: ShieldCheck, label: "Gatekeeper", tone: "border-blue-500/50 bg-blue-500/10 text-blue-300" },
    budget_owner: { icon: Wallet, label: "Budget owner", tone: "border-purple-500/50 bg-purple-500/10 text-purple-300" },
    technical_evaluator: { icon: Wrench, label: "Technical evaluator", tone: "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" },
};

export default function Stakeholders() {
    const { id: firmId } = useParams();
    const [firm, setFirm] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get(`/firms/${firmId}`).then(({ data }) => setFirm(data)).catch(() => {});
    }, [firmId]);

    const run = async () => {
        setLoading(true); setError(""); setResult(null);
        try {
            const { data: enqueued } = await api.post(`/firms/${firmId}/stakeholders`);
            const r = await pollJob(enqueued.job_id, { timeoutMs: 5 * 60 * 1000 });
            setResult(r);
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setError(rl || (typeof d === "string" ? d : (e.jobError ? e.message : "Stakeholder map failed")));
        } finally {
            setLoading(false);
        }
    };

    const linkedInSearch = (title) => `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(`${title} ${firm?.name || ""}`)}&origin=GLOBAL_SEARCH_HEADER`;

    if (!firm) return <div className="p-12"><Loader label="Loading firm" /></div>;

    return (
        <div className="p-8" data-testid="stakeholders-page">
            <div className="mb-6 flex items-center justify-between">
                <Link to={`/app/firms/${firmId}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-amber-500" data-testid="back-to-firm">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to {firm.name}
                </Link>
            </div>

            <header className="mb-6 border-b border-zinc-800 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Stakeholder map</div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">
                    Who to contact at <span className="text-amber-500">{firm.name}</span>?
                </h1>
                <p className="text-sm text-zinc-500 mt-2 max-w-3xl">
                    AI maps the org. Get the five key roles you need to engage to land the deal — decision maker, champion, gatekeeper, budget owner, technical evaluator — each with a likely job title and the exact angle to lead with on first contact.
                </p>
            </header>

            <div className="border border-zinc-800 bg-zinc-950 p-5 mb-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-zinc-300">One AI call. ~5 stakeholders, each with a LinkedIn-searchable title.</div>
                <button
                    onClick={run}
                    disabled={loading}
                    data-testid="run-stakeholders-btn"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-5 py-3"
                >
                    <Users className="w-4 h-4" /> {loading ? "Mapping…" : result ? "Refresh" : "Map stakeholders"}
                </button>
            </div>

            {error && <div data-testid="stakeholders-error" className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6">{error}</div>}
            {loading && <div className="border border-zinc-800 bg-zinc-950 p-12"><Loader label="Mapping the org" stages={LoaderStages.stakeholders} /></div>}

            {result && result.stakeholders && result.stakeholders.length > 0 && (
                <div className="space-y-3" data-testid="stakeholders-results">
                    {result.stakeholders.map((st, i) => {
                        const meta = ROLE_META[st.role] || { icon: KeyRound, label: st.role || "Contact", tone: "border-zinc-700 bg-zinc-900/40 text-zinc-300" };
                        const Icon = meta.icon;
                        return (
                            <article key={i} className="border border-zinc-800 bg-zinc-950 p-5" data-testid={`stakeholder-row-${i}`}>
                                <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                                    <div className="flex items-center gap-3">
                                        <div className="font-mono text-[10px] text-zinc-600">{String(st.priority).padStart(2, "0")}</div>
                                        <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${meta.tone}`}>
                                            <Icon className="w-3 h-3" /> {meta.label}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-heading font-bold text-zinc-50 text-lg">{st.title_pattern}</div>
                                        <div className="font-mono text-[10px] text-zinc-500">{st.department}</div>
                                    </div>
                                </div>
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Why they matter</div>
                                        <p className="text-sm text-zinc-300 leading-relaxed">{st.why_they_matter}</p>
                                    </div>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">First-contact angle</div>
                                        <p className="text-sm text-zinc-200 leading-relaxed">{st.first_contact_strategy}</p>
                                    </div>
                                </div>
                                {st.alt_titles && st.alt_titles.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">LinkedIn search:</span>
                                        <a
                                            href={linkedInSearch(st.title_pattern)}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`linkedin-primary-${i}`}
                                            className="text-[11px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 px-2 py-0.5 transition-colors"
                                        >
                                            {st.title_pattern} →
                                        </a>
                                        {st.alt_titles.map((t, j) => (
                                            <a key={j} href={linkedInSearch(t)} target="_blank" rel="noreferrer"
                                                className="text-[11px] font-mono border border-zinc-700 hover:border-amber-500 text-zinc-400 hover:text-amber-400 px-2 py-0.5 transition-colors">
                                                {t} →
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            {result && (!result.stakeholders || result.stakeholders.length === 0) && !loading && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="text-sm text-zinc-500">AI returned no stakeholders. Try refreshing.</div>
                </div>
            )}

            {!result && !loading && !error && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">Click "Map stakeholders" to begin</div>
                </div>
            )}
        </div>
    );
}
